import React, { useContext, useEffect, useState } from 'react'
import { Modal, Form, Select, Button, Radio, Upload, Input, Checkbox } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import openNotification from 'utils/Notification'
import { BULK_UPDATE_TICKETS, NEW_UPDATE_TICKET } from '../graphql/Mutations'
import { useMutation } from '@apollo/react-hooks'
import { AppContext } from 'AppContext'

const { Option } = Select
const STATUS_OPTIONS = [
  //'OPEN',
  //'BREACHED',
  //'ASSIGNED',
  //'TRIAGED',
  //'ESCALATED',
  'INPROGRESS',
  //'REVIEW',
  'RESOLVED',
  'CLOSED',
]

const UpdateBulkActions = ({
  visible,
  onCancel,
  loading,
  selectedTickets,
  usersData,
}) => {
  const [form] = Form.useForm()
  const [closeForm] = Form.useForm()
  const [mode, setMode] = useState('agent')
  const [users, setUsers] = useState(usersData || [])
  const [statuses, setStatuses] = useState([])
  const [loadingData, setLoadingData] = useState(false)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [closeLoading, setCloseLoading] = useState(false)
  const [ticketIdsToClose, setTicketIdsToClose] = useState([])
  const [useSameDescription, setUseSameDescription] = useState(false)
  const {
    state: { currentUser },
  } = useContext(AppContext)
  const [bulkUpdateTickets] = useMutation(BULK_UPDATE_TICKETS)
  const [updateTicket] = useMutation(NEW_UPDATE_TICKET)

  useEffect(() => {
    setLoadingData(true)
    if (mode === 'agent') {
      setUsers(users)
      setLoadingData(false)
    } else if (mode === 'status') {
      if (currentUser?.role === 'STAFF') {
        setStatuses(STATUS_OPTIONS.filter((s) => ['OPEN', 'RESOLVED'].includes(s)))
      } else {
        setStatuses(STATUS_OPTIONS)
      }
      setLoadingData(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, users])

  const onFinish = async (values) => {
    if (!selectedTickets || selectedTickets.length === 0) {
      openNotification('error', 'Please select at least one ticket')
      return
    }

    const ticketIds = selectedTickets

    const data = {
      ticketIds,
      assignUserId: mode === 'agent' ? +values.assignUserId : null,
      status: mode === 'status' ? values.statusId : null,
    }
    // If closing tickets, collect per-ticket descriptions instead of bulk update
    if (mode === 'status' && values.statusId === 'CLOSED') {
      setTicketIdsToClose(ticketIds)
      // open modal to collect per-ticket descriptions
      setShowCloseModal(true)
      return
    }

    try {
      const res = await bulkUpdateTickets({ variables: { data } })

      if (res.data.bulkUpdateTickets.success) {
        openNotification('success', res.data.bulkUpdateTickets.message)

        if (data.assignUserId) {
          const assignedUser = (users || []).find((u) => +u.id === +data.assignUserId)
          const assignPayload = {
            ticketIds,
            assignUser: assignedUser
              ? {
                  id: assignedUser.id,
                  firstName: assignedUser.firstName,
                  lastName: assignedUser.lastName,
                  role: assignedUser.role,
                }
              : { id: +data.assignUserId },
          }
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('bulkAssignApplied', { detail: assignPayload }))
          }
        }

        if (data.status) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('bulkStatusApplied', { detail: { ticketIds, status: data.status } }))
          }
        }

        form.resetFields()
        onCancel()

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('clearSelectedRowKeys'))
          setTimeout(() => window.dispatchEvent(new CustomEvent('refetchTickets')), 700)
          window.dispatchEvent(new CustomEvent('refetchExpandedTickets', { detail: ticketIds }))
        }
      } else {
        openNotification('error', res.data.bulkUpdateTickets.message)
      }
    } catch (err) {
      openNotification('error', err.message)
    }
  }

  // Close multiple tickets individually with different descriptions
  const handleCloseAll = async (values) => {
    let descriptions = values?.descriptions || []
    // If using same description for all tickets, replicate common into descriptions
    if (useSameDescription) {
      const common = values?.common || {}
      descriptions = ticketIdsToClose.map(() => common)
    }
    if (!ticketIdsToClose || ticketIdsToClose.length === 0) {
      openNotification('error', 'No tickets to close')
      return
    }
    setCloseLoading(true)
    try {
      // Close tickets one by one so each can have its own description
      for (let i = 0; i < ticketIdsToClose.length; i++) {
        const id = ticketIdsToClose[i]
        const descObj = descriptions[i] || {}
        let files = []
        if (descObj?.fileUrl) {
          descObj.fileUrl.forEach((file) => {
            files.push(file?.originFileObj)
          })
        } else {
          // no attachments for this ticket
        }

        // each update: status CLOSED with reason, closeById and attachments
        // eslint-disable-next-line no-await-in-loop
        await updateTicket({
          variables: {
            id,
            data: {
              reason: descObj.reason || '',
              status: 'CLOSED',
              closeById: currentUser.id,
              fileUrl: files,
            },
          },
        })
      }

      openNotification('success', 'Tickets Closed Successfully')
      setShowCloseModal(false)
      setTicketIdsToClose([])
      closeForm.resetFields()
      form.resetFields()
      onCancel()
      // clear selection in parent table
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('clearSelectedRowKeys'))
        // ask top-level ticket lists to refetch expanded details for these tickets
        window.dispatchEvent(
          new CustomEvent('refetchExpandedTickets', { detail: ticketIdsToClose }),
        )
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('refetchTickets'))
      }
    } catch (err) {
      openNotification('error', err.message)
    } finally {
      setCloseLoading(false)
    }
  }
  return (
    <Modal
      visible={visible}
      maskClosable={false}
      title='Bulk Actions'
      footer={null}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      onCancel={() => {
        form.resetFields()
        onCancel()
      }}
    >
      <Form form={form} layout='vertical' onFinish={onFinish}>
        <Form.Item>
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
            <Radio value='agent'>Assign Agent</Radio>
            <Radio value='status'>Change Status</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={mode === 'agent' ? 'Agent Name' : 'Status'}
          name={mode === 'agent' ? 'assignUserId' : 'statusId'}
          rules={[{ required: true, message: `Please select a ${mode}` }]}
        >
          <Select
            loading={loadingData}
            placeholder={`Select ${mode === 'agent' ? 'Agent' : 'Status'}`}
            showSearch
            filterOption={(input, option) => {
              const label =
                option?.label ||
                (typeof option?.children === 'string' ? option.children : '')
              return String(label).toLowerCase().includes(String(input).toLowerCase())
            }}
          >
            {mode === 'agent'
              ? users
                  .filter((user) => user.id !== currentUser.id)
                  .map((u) => (
                    <Option
                      key={u.id}
                      value={u.id}
                      label={`${u.firstName} ${u.lastName} (${u.role})`}
                    >
                      <span
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <span>
                          {u.firstName} {u.lastName}
                        </span>
                        <span
                          style={{
                            marginLeft: 8,
                            color: '#999',
                          }}
                        >
                          ({u.role})
                        </span>
                      </span>
                    </Option>
                  ))
              : statuses.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
          </Select>
        </Form.Item>

        <Form.Item style={{ textAlign: 'center' }}>
          <Button type='primary' htmlType='submit' loading={loading}>
            Submit
          </Button>
        </Form.Item>
      </Form>
      {/* Modal to collect per-ticket descriptions when closing multiple tickets */}
      <Modal
        visible={showCloseModal}
        title='Close Tickets'
        maskClosable={false}
        footer={null}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
        onCancel={() => {
          setShowCloseModal(false)
          closeForm.resetFields()
        }}
      >
        <Form form={closeForm} layout='vertical' onFinish={handleCloseAll}>
          <Form.Item>
            <Checkbox
              checked={useSameDescription}
              onChange={(e) => setUseSameDescription(e.target.checked)}
            >
              Use same description & attachments for all selected tickets
            </Checkbox>
          </Form.Item>

          {useSameDescription ? (
            <>
              <Form.Item
                name={['common', 'reason']}
                label={`Closing Description for all selected tickets`}
                rules={[
                  {
                    required: true,
                    message: 'Please write your description!',
                    whitespace: true,
                  },
                ]}
                hasFeedback
              >
                <Input.TextArea rows={5} />
              </Form.Item>

              <Form.Item
                name={['common', 'fileUrl']}
                label='Attachments for all selected tickets'
                valuePropName='fileList'
                getValueFromEvent={(e) => {
                  if (!e) return []
                  return Array.isArray(e) ? e : e.fileList
                }}
              >
                <Upload beforeUpload={() => false} multiple>
                  <Button icon={<UploadOutlined />}>Attach files</Button>
                </Upload>
              </Form.Item>
            </>
          ) : (
            ticketIdsToClose &&
            ticketIdsToClose.map((tid, idx) => (
              <div key={tid}>
                <Form.Item
                  name={['descriptions', idx, 'reason']}
                  label={`Ticket ${tid} - Closing Description`}
                  rules={[
                    {
                      required: true,
                      message: 'Please write description for this ticket',
                      whitespace: true,
                    },
                  ]}
                >
                  <Input.TextArea rows={5} />
                </Form.Item>

                <Form.Item
                  name={['descriptions', idx, 'fileUrl']}
                  label='Attachments'
                  valuePropName='fileList'
                  getValueFromEvent={(e) => {
                    if (!e) return []
                    return Array.isArray(e) ? e : e.fileList
                  }}
                >
                  <Upload beforeUpload={() => false} multiple>
                    <Button icon={<UploadOutlined />}>Attach files</Button>
                  </Upload>
                </Form.Item>
              </div>
            ))
          )}

          <Form.Item style={{ textAlign: 'center' }}>
            <Button type='primary' htmlType='submit' loading={closeLoading}>
              Close All
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  )
}

export default UpdateBulkActions
